import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileX2, SearchX } from "lucide-react";
import {
  Badge,
  FeedbackState,
  localTablePage,
  nextSort,
  type DataTableColumn,
  type TableSort,
} from "@h2j-data-team/ui";
import type { OrphanQvd } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import { formatBytes } from "@/lib/health-format";
import { qvdOrphansInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import {
  FileFormatBadge,
  ListError,
  ListLoading,
  QvdListToolbar,
  QvdPathLabel,
  SpaceLink,
} from "./qvd-list-parts";
import { ALL_ROWS } from "./qvd-list-utils";

const DEFAULT_SORT: TableSort = { id: "size", direction: "desc" };

export function OrphanQvdsTable({
  runId,
  orphanCount,
  reclaimableBytes,
  familyOnlyCount = 0,
  familyOnlyBytes = 0,
  baseUrl,
}: {
  runId: string;
  orphanCount?: number;
  reclaimableBytes?: number;
  // The badged "may be in use" subset, discounted from the summary exactly as
  // the KPI strip discounts it — the two must never disagree.
  familyOnlyCount?: number;
  familyOnlyBytes?: number;
  baseUrl?: string | null;
}) {
  const { t } = useTranslation("qvd-analysis");
  const { formatDate } = useFormatDate();
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(qvdOrphansInfiniteQueryOptions(runId));

  // Load every page so filtering + sorting operate over the full result set.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<TableSort>(DEFAULT_SORT);

  const orphans = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  const spaceOptions = useMemo(
    () =>
      Array.from(new Set(orphans.map((o) => o.spaceName).filter((s): s is string => !!s))).sort(),
    [orphans],
  );

  const columns = useMemo<DataTableColumn<OrphanQvd>[]>(
    () => [
      {
        id: "name",
        header: t("orphans.cols.file"),
        sort: "text",
        value: (o) => o.qvdName,
        cell: (o) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1">
              <QvdPathLabel path={o.qvdPath} />
            </span>
            <FileFormatBadge path={o.qvdPath} />
            {/* Consumption WAS found for this file, only inferred from its name
                family — it is listed so the user can judge it, not presented as
                "nobody reads it". */}
            {o.consumedByFamilyOnly && (
              <Badge
                variant="secondary"
                className="shrink-0 py-0"
                title={t("orphans.familyOnlyHint")}
              >
                {t("orphans.familyOnly")}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: "space",
        header: t("orphans.cols.space"),
        width: 180,
        sort: "text",
        value: (o) => o.spaceName,
        cell: (o) => (
          <span className="text-dim">
            <SpaceLink spaceName={o.spaceName} spaceId={o.spaceId} baseUrl={baseUrl} />
          </span>
        ),
      },
      {
        id: "modified",
        header: t("orphans.cols.lastModified"),
        width: 140,
        sort: "date",
        value: (o) => o.lastModifiedAt,
        cell: (o) => (
          <span className="text-dim">{o.lastModifiedAt ? formatDate(o.lastModifiedAt) : "—"}</span>
        ),
      },
      {
        id: "size",
        header: t("orphans.cols.size"),
        width: 110,
        align: "end",
        variant: "numeric",
        sort: "number",
        value: (o) => o.sizeBytes,
        cell: (o) => (o.sizeBytes != null ? formatBytes(o.sizeBytes) : "—"),
      },
    ],
    [t, formatDate, baseUrl],
  );

  const rows = useMemo(
    () =>
      localTablePage(
        orphans,
        {
          page: 1,
          pageSize: ALL_ROWS,
          query: search,
          filters: { spaces: spaceFilter },
          sort,
          selection: [],
        },
        {
          columns,
          search: (o) => `${o.qvdPath} ${o.spaceName ?? ""}`,
          filter: (o, f) =>
            f.spaces.length === 0 || (o.spaceName != null && f.spaces.includes(o.spaceName)),
        },
      ).rows,
    [orphans, columns, search, spaceFilter, sort],
  );

  if (isLoading) return <ListLoading />;
  if (isError) {
    return <ListError error={error} title={t("orphans.error")} onRetry={() => void refetch()} />;
  }
  if (orphans.length === 0) {
    return (
      <FeedbackState
        state="empty"
        icon={<FileX2 />}
        title={t("orphans.empty.title")}
        description={t("orphans.empty.description")}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <QvdListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t("orphans.searchPlaceholder")}
        spaceOptions={spaceOptions}
        spaceFilter={spaceFilter}
        onSpaceFilter={setSpaceFilter}
      />

      <p className="pb-2 text-sm text-dim">
        {familyOnlyCount > 0
          ? t("orphans.summaryFamilyOnly", {
              count: Math.max((orphanCount ?? orphans.length) - familyOnlyCount, 0),
              size: formatBytes(Math.max((reclaimableBytes ?? 0) - familyOnlyBytes, 0)),
              familyOnly: familyOnlyCount,
            })
          : t("orphans.summary", {
              count: orphanCount ?? orphans.length,
              size: formatBytes(reclaimableBytes ?? 0),
            })}
      </p>

      {rows.length === 0 ? (
        <FeedbackState
          state="no-results"
          icon={<SearchX />}
          title={t("files.empty.filteredTitle")}
          description={t("files.empty.filteredDescription")}
        />
      ) : (
        <VirtualRowList
          label={t("tabs.orphans")}
          columns={columns}
          rows={rows}
          getKey={(o) => o.id}
          sort={sort}
          onSort={(column) => setSort(nextSort(sort, column.id, column.sort) ?? DEFAULT_SORT)}
        />
      )}
    </div>
  );
}
